import { useEffect, useRef, useState } from 'react'
import type { CommentThread } from '@w/content-blocks'
import { Avatar } from '@w/ui/components/avatar'
import { CheckIcon, MessageSquareIcon, MoreHorizontalIcon, PencilIcon, SendIcon, Trash2Icon } from '@w/ui/components/icon'
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@w/ui/components/menu'
import { Sheet, SheetPopup, SheetTitle, SheetTrigger } from '@w/ui/components/sheet'
import { relativeCommentTime } from './review-comment-time'

export interface PageCommentsConfig {
  /** A host-provided add-comment action controls the composer instead of the editor's default action. */
  composer?: { open: boolean; onOpenChange: (open: boolean) => void }
  threads: readonly CommentThread[]
  currentAuthor: { name: string; avatarUrl?: string }
  resolveAuthor: (authorId: string) => { name: string; avatarUrl?: string }
  onAdd: (body: string) => void
  onReply: (threadId: string, body: string) => void
  onEdit: (threadId: string, messageId: string, body: string) => void
  onDelete: (threadId: string, messageId: string) => void
  onResolve: (threadId: string) => void
  onReopen?: (threadId: string) => void
}

export function PageCommentAction({ onClick }: { onClick: () => void }) {
  const [actionsOpen, setActionsOpen] = useState(false)
  return <>
    <button type="button" onClick={onClick} data-testid="page-comment-add-action"
      className="text-on-surface-secondary hover:text-on-surface hidden items-center gap-1.5 text-sm min-[1026px]:flex">
      <MessageSquareIcon className="size-4" /> Add comment
    </button>
    <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
      <SheetTrigger data-testid="page-comment-mobile-actions" aria-label="Page actions" className="text-on-surface-secondary hover:text-on-surface min-[1026px]:hidden"><MoreHorizontalIcon className="size-5" /></SheetTrigger>
      <SheetPopup side="bottom" className="p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <SheetTitle className="mb-5 text-center">Actions</SheetTitle>
        <button type="button" data-testid="page-comment-mobile-add" className="hover:bg-surface-3 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left" onClick={() => { setActionsOpen(false); onClick() }}>
          <MessageSquareIcon className="size-5" /> Add comment
        </button>
      </SheetPopup>
    </Sheet>
  </>
}

export function PageComments({ config, composing, onComposingChange }: {
  config: PageCommentsConfig
  composing: boolean
  onComposingChange: (open: boolean) => void
}) {
  const [body, setBody] = useState('')
  const input = useRef<HTMLTextAreaElement>(null)
  const [active, setActive] = useState<{ kind: 'reply' | 'edit'; threadId: string; messageId?: string } | null>(null)
  const [showResolved, setShowResolved] = useState(false)
  useEffect(() => { if (composing) input.current?.focus() }, [composing])
  const threads = config.threads.filter(thread => thread.status === 'open' || showResolved)
  const submit = () => {
    const value = body.trim()
    if (!value) return
    if (active?.kind === 'edit' && active.messageId) config.onEdit(active.threadId, active.messageId, value)
    else if (active?.kind === 'reply') config.onReply(active.threadId, value)
    else config.onAdd(value)
    setBody('')
    setActive(null)
    onComposingChange(false)
  }
  const start = (target: typeof active, value = '') => {
    setActive(target)
    setBody(value)
    onComposingChange(true)
  }
  if (threads.length === 0 && !composing && config.threads.length === 0) return null
  return <section contentEditable={false} data-testid="page-comments" className="border-separator mb-12 border-b pb-5">
    <div className="flex flex-col gap-5">
      {threads.map(thread => <div key={thread.threadId} data-testid={`page-comment-thread-${thread.threadId}`} className="group/thread">
        {thread.comments.map((message, index) => {
          const author = config.resolveAuthor(message.authorId)
          return <div key={message.id} className="border-separator relative flex gap-3 pb-4 pt-1 after:absolute after:bottom-0 after:left-3 after:top-9 after:border-l">
            <Avatar size="xs" src={author.avatarUrl} name={author.name} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex min-h-6 items-center gap-2 text-sm"><span className="font-medium">{author.name}</span><span className="text-on-surface-secondary text-xs">{relativeCommentTime(message.createdAt, Date.now())}</span></div>
              <div className="text-body-medium mt-1 whitespace-pre-wrap break-words">{message.body}</div>
              {index === thread.comments.length - 1 && thread.status === 'open' && <button type="button" data-testid="page-comment-reply" className="text-on-surface-secondary hover:text-on-surface mt-2 text-xs" onClick={() => { start({ kind: 'reply', threadId: thread.threadId }) }}>Reply</button>}
            </div>
            <div className="bg-surface-2 border-separator absolute top-0 right-0 flex rounded-md border opacity-0 transition-opacity group-hover/thread:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
              {thread.status === 'open' && <button type="button" aria-label="Reply" className="hover:bg-surface-3 p-1.5" onClick={() => { start({ kind: 'reply', threadId: thread.threadId }) }}><MessageSquareIcon className="size-4" /></button>}
              {thread.status === 'open' && <button type="button" aria-label="Resolve comment" data-testid="page-comment-resolve" className="hover:bg-surface-3 p-1.5" onClick={() => { config.onResolve(thread.threadId) }}><CheckIcon className="size-4" /></button>}
              {thread.status === 'resolved' && config.onReopen && <button type="button" aria-label="Reopen comment" className="hover:bg-surface-3 p-1.5" onClick={() => config.onReopen?.(thread.threadId)}><MessageSquareIcon className="size-4" /></button>}
              <Menu><MenuTrigger aria-label="Comment actions" className="hover:bg-surface-3 p-1.5"><MoreHorizontalIcon className="size-4" /></MenuTrigger><MenuPopup align="end">
                <MenuItem onClick={() => { start({ kind: 'edit', threadId: thread.threadId, messageId: message.id }, message.body) }}><PencilIcon />Edit</MenuItem>
                <MenuItem onClick={() => { config.onDelete(thread.threadId, message.id) }}><Trash2Icon />Delete</MenuItem>
              </MenuPopup></Menu>
            </div>
          </div>
        })}
      </div>)}
    </div>
    {composing && <div className="mt-4 flex items-start gap-3" data-testid="page-comment-composer">
      <Avatar size="xs" src={config.currentAuthor.avatarUrl} name={config.currentAuthor.name} className="shrink-0" />
      <div className="flex min-w-0 flex-1 items-end gap-2">
        <textarea ref={input} aria-label="Add a comment" data-testid="page-comment-input" value={body} onChange={event => { setBody(event.currentTarget.value) }}
          onBlur={() => { if (!body.trim()) onComposingChange(false) }}
          onKeyDown={event => {
            if (event.key === 'Escape') { setBody(''); setActive(null); onComposingChange(false); return }
            if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit() }
          }}
          placeholder="Add a comment…" rows={1} className="text-body-medium min-h-8 flex-1 resize-y bg-transparent py-1 outline-none" />
        <button type="button" aria-label="Send comment" data-testid="page-comment-submit" disabled={!body.trim()} onClick={submit}
          className="bg-on-surface text-surface-1 disabled:bg-surface-3 disabled:text-on-surface-secondary flex size-7 shrink-0 items-center justify-center rounded-full"><SendIcon className="size-4" /></button>
      </div>
    </div>}
    {!composing && <button type="button" data-testid="page-comment-composer-trigger" onClick={() => { start(null) }} className="text-on-surface-secondary hover:text-on-surface mt-5 flex w-full items-center gap-3 text-left text-sm">
      <Avatar size="xs" src={config.currentAuthor.avatarUrl} name={config.currentAuthor.name} />Add a comment…
    </button>}
    {config.threads.some(thread => thread.status === 'resolved') && <button type="button" className="text-on-surface-secondary hover:text-on-surface mt-3 text-xs" onClick={() => { setShowResolved(value => !value) }}>{showResolved ? 'Hide resolved' : 'Show resolved'}</button>}
  </section>
}
